import React, { useState, useEffect, useRef, useLayoutEffect, useCallback } from 'react';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { logout } from '../firebase/auth';
import AvatarMaker from "../components/avatar/AvatarMaker";
import DashboardHero from "../components/dashboard/DashboardHero";
import "../components/dashboard/dashboard.css";
import CoinCounter from "../components/coins/CoinCounter";
import { useWallet } from "../services/coinService";
import { formalName } from "../utils/displayName";
import ShopButton from "../components/shop/ShopButton";
import BuyCoinsButton from "../components/coins/BuyCoinsButton";
import LowClassBank from "../components/coins/LowClassBank";
import EducatorDailyCoins from "../components/coins/EducatorDailyCoins";
import EducatorPromos from "../components/coins/EducatorPromos";
import LeaderboardButton from "../components/leaderboard/LeaderboardButton";
import "../components/coins/coins.css";
import TrialOfferModal from "../components/TrialOfferModal";
import { evaluateTrial } from "../utils/trialOffer";
import { logPageView } from "../utils/logPageView";
import "./global.css"; 
import { checkAndExpireTrial } from "../utils/checkTrialExpiration";
import useAccessLevel from "../hooks/useAccessLevel";
import { gradeToken } from "../config/plans";
import FreeTierBanner from "../components/FreeTierBanner";
import ProductSpotlight from "../components/ProductSpotlight";
import SubscribeNow from "../components/SubscribeNow";
import RefreshAccessButton from "../components/RefreshAccessButton";
import { NAV_SUBJECTS } from "../data/subjects";
import {
  ensureEducatorCode as ensureCode,
  refreshDirectoryEntry,
} from "../utils/educatorCode";
import { effectiveRoleKind, ROLE_KIND } from "../utils/accountRole";
import "../styles/sbl-ui.css";
import { httpsCallable, getFunctions } from "firebase/functions";
import { functions } from "../firebase/firebaseConfig";

/** -----------------------
 *  Reusable overlay hint
 *  ----------------------*/
const HintsOverlay = ({
  targets,           // { key: { ref, text, placement } }
  onDismiss,         // () => void
  canDismiss,        // boolean
}) => {
  const [positions, setPositions] = useState({});

  const compute = () => {
    const next = {};
    Object.entries(targets).forEach(([key, { ref, placement = 'top' }]) => {
      const el = ref?.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // default bubble position near target with a little offset
      const margin = 10;
      let top = rect.top - margin;
      let left = rect.left + rect.width / 2;

      if (placement === 'top') {
        top = rect.top - margin;
        left = rect.left + rect.width / 2;
      } else if (placement === 'right') {
        top = rect.top + rect.height / 2;
        left = rect.right + margin;
      } else if (placement === 'left') {
        top = rect.top + rect.height / 2;
        left = rect.left - margin;
      } else if (placement === 'bottom') {
        top = rect.bottom + margin;
        left = rect.left + rect.width / 2;
      }

      next[key] = { top, left, rect, placement };
    });
    setPositions(next);
  };

  useLayoutEffect(() => {
    compute();
    const onResize = () => compute();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [targets]);
  

  return (
    <>
      {/* screen-wide overlay container */}
      <div style={overlayStyles.layer} aria-hidden>
        {Object.entries(targets).map(([key, { text }]) => {
          const pos = positions[key];
          if (!pos) return null;

          // compute bubble top/left from centerpoint
          const bubbleOffset = 14; // px away from the target edge
          const bubbleStyle = { ...overlayStyles.bubble };
          const arrowStyle = { ...overlayStyles.arrow };

          let bubbleTop = pos.top;
          let bubbleLeft = pos.left;

          if (pos.placement === 'top') {
            bubbleTop = pos.rect.top - 8 - bubbleOffset;
            bubbleLeft = pos.rect.left + pos.rect.width / 2;
            arrowStyle.borderTopColor = '#111';
            arrowStyle.top = bubbleTop + overlayStyles.bubble.padding + 28;
            arrowStyle.left = bubbleLeft;
            arrowStyle.transform = 'translate(-50%, 0) rotate(180deg)';
          } else if (pos.placement === 'bottom') {
            bubbleTop = pos.rect.bottom + bubbleOffset;
            bubbleLeft = pos.rect.left + pos.rect.width / 2;
            arrowStyle.borderTopColor = '#111';
            arrowStyle.top = bubbleTop - 8;
            arrowStyle.left = bubbleLeft;
            arrowStyle.transform = 'translate(-50%, 0)';
          } else if (pos.placement === 'right') {
            bubbleTop = pos.rect.top + pos.rect.height / 2;
            bubbleLeft = pos.rect.right + bubbleOffset;
            arrowStyle.borderRightColor = '#111';
            arrowStyle.top = bubbleTop;
            arrowStyle.left = bubbleLeft - 8;
            arrowStyle.transform = 'translate(0, -50%) rotate(-90deg)';
          } else if (pos.placement === 'left') {
            bubbleTop = pos.rect.top + pos.rect.height / 2;
            bubbleLeft = pos.rect.left - bubbleOffset;
            arrowStyle.borderLeftColor = '#111';
            arrowStyle.top = bubbleTop;
            arrowStyle.left = bubbleLeft + 8;
            arrowStyle.transform = 'translate(0, -50%) rotate(90deg)';
          }

          bubbleStyle.top = bubbleTop;
          bubbleStyle.left = bubbleLeft;
          bubbleStyle.transform = 'translate(-50%, -100%)'; // nice default; overridden by placements below

          if (pos.placement === 'bottom') bubbleStyle.transform = 'translate(-50%, 0)';
          if (pos.placement === 'right')  bubbleStyle.transform = 'translate(0, -50%)';
          if (pos.placement === 'left')   bubbleStyle.transform = 'translate(-100%, -50%)';

          return (
            <React.Fragment key={key}>
              <div style={arrowStyle} />
              <div style={bubbleStyle}>
                {text}
              </div>
            </React.Fragment>
          );
        })}
      </div>

      {/* control pill */}
      {canDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          style={overlayStyles.dismissButton}
        >
          Hide Tips
        </button>
      )}
    </>
  );
};

const overlayStyles = {
  layer: {
    position: 'fixed',
    inset: 0,
    pointerEvents: 'none', // do not block UI
    zIndex: 9999,
  },
  bubble: {
    position: 'fixed',
    maxWidth: 320,
    background: '#111',
    color: '#fff',
    fontSize: 14,
    lineHeight: 1.35,
    padding: 12,
    borderRadius: 8,
    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
    border: '2px solid #fff',
    pointerEvents: 'auto', // allow text selection if needed
  },
  arrow: {
    position: 'fixed',
    width: 0,
    height: 0,
    borderLeft: '8px solid transparent',
    borderRight: '8px solid transparent',
    borderTop: '8px solid #111', // default pointing down; we rotate per placement
    pointerEvents: 'none',
    zIndex: 9999,
  },
  dismissButton: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 10000,
    background: '#222',
    color: '#fff',
    border: '2px solid #fff',
    borderRadius: 999,
    padding: '10px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
  },
  showButton: {
    position: 'fixed',
    bottom: 20,
    right: 20,
    zIndex: 10000,
    background: '#0d9488', // teal so it looks different
    color: '#fff',
    border: '2px solid #fff',
    borderRadius: 999,
    padding: '10px 16px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 6px 14px rgba(0,0,0,0.35)',
  },
};

// The sentence under the teacher's name: what to do next, with live numbers.
const EducatorHeroLine = ({ code }) => {
  const w = useWallet();
  const bank = Number(w.classBank) || 0;
  return (
    <>
      {code ? (
        <>
          Share class code <b>{code}</b> so your students join your Classroom.{" "}
        </>
      ) : (
        "Your class code is on its way. "
      )}
      Your class coin bank has <b>{bank.toLocaleString()} coin{bank === 1 ? "" : "s"}</b> to hand out with <b>Give coins</b>.
    </>
  );
};

const CopyCode = ({ code }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        try {
          navigator.clipboard.writeText(String(code));
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* no clipboard */
        }
      }}
    >
      {done ? "Copied ✓" : "Copy"}
    </button>
  );
};

const DashboardEducator = () => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [showAvatarPopup, setShowAvatarPopup] = useState(false);

  // Tips always start hidden — on every visit and every refresh. They show
  // only after "Show Tips" is clicked, and only until the page is left.
  const [hideTips, setHideTips] = useState(true);
  const [showTutorial, setShowTutorial] = useState(false);

  // The gate used to be `userData?.trial?.status === "expired"` and nothing
  // else. isUserBlocked() below — the only code in this file that looked at
  // subscription.status — was defined but never called, so paying for a
  // subscription did nothing to unlock this page: trial.status stays
  // "expired" forever and the dashboard stayed locked after payment.
  //
  // useAccessLevel resolves the subscription document, an assigned seat, and
  // the admin claim together, so any of the three now clears the lock.
  const { isPaid, isFree, hasPaidSubscription, loading: accessLoading } =
    useAccessLevel();

  // ---- Trial extension offer -------------------------------------------
  // Must sit AFTER the useAccessLevel destructure above — it reads isPaid,
  // and a const referenced before its declaration is a ReferenceError, not a
  // warning.
  //
  // The offer window opens 5 days out and keeps showing until it's taken or
  // the trial ends. See utils/trialOffer.js for why the local trial clock and
  // the Stripe one must never both be running.
  const [trialOfferDismissed, setTrialOfferDismissed] = useState(false);
  const trialState = evaluateTrial(userData || {}, {
    hasPaidSubscription: Boolean(hasPaidSubscription),
  });

  // Backfill the educator code for accounts created before it was assigned
  // at signup — that's why this page showed "Educator Code: undefined".
  // Idempotent: the callable returns the existing code if there is one.
  // status: "idle" | "working" | "error". The old version tracked a single
  // `codeBusy` boolean AND listed it as a dependency, which made the effect
  // re-trigger itself: busy=true re-ran the effect, the `finally` set
  // busy=false, that re-ran it again, and because a failed call leaves
  // educatorCode empty it called straight back out. A failure became an
  // infinite retry against the Cloud Function, and the heading sat on
  // "Assigning your educator code…" forever because the flag never settled.
  //
  // A ref guards the attempt instead of state, so it runs once per mount and
  // is never itself a dependency. Retrying is now something the person
  // chooses, not something the render loop does thousands of times.
  const [codeState, setCodeState] = useState({ status: "idle", error: null });
  const codeAttempted = useRef(false);

  const assignEducatorCode = useCallback(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid) return;

    setCodeState({ status: "working", error: null });

    // Allocated in the browser via a counter transaction — see
    // utils/educatorCode.js. This used to call the ensureEducatorCode
    // callable, which the browser can't reach.
    ensureCode(uid, userData || {})
      .then((code) => {
        if (code) {
          setUserData((prev) => ({ ...prev, educatorCode: code }));
          setCodeState({ status: "idle", error: null });
          return;
        }
        setCodeState({ status: "error", error: "No code was assigned." });
      })
      .catch((err) => {
        console.error("[educatorCode] allocation failed:", err);
        setCodeState({ status: "error", error: describeCodeError(err) });
      });
  }, [userData]);

  const retryEducatorCode = () => {
    codeAttempted.current = true;
    assignEducatorCode();
  };

  // ==========================================================================
  // THE REASON IT SAID "Code pending" FOREVER
  // ==========================================================================
  // assignEducatorCode() existed, retryEducatorCode() existed, the error
  // panel with its Try again button existed — and nothing ever called any of
  // them. There was no mount effect.
  //
  // That made a closed loop: the only trigger was the Try again button, and
  // that button only renders when codeState.status === "error", which can
  // only be set by an attempt that never happened. So the allocation code was
  // unreachable and the badge sat on "Code pending" indefinitely. It wasn't
  // failing — it was never starting.
  //
  // The ref guard is the important part of the fix. codeState must NOT be a
  // dependency here: a failed attempt sets state, which would re-run the
  // effect, which would attempt again, forever. One attempt per mount, and
  // retrying is something the educator chooses.
  useEffect(() => {
    if (!userData) return;                 // profile hasn't loaded yet
    if (userData.educatorCode) return;     // already has one
    if (codeAttempted.current) return;     // one attempt per mount

    // Students and parents type someone else's code; they don't get one.
    if (effectiveRoleKind(userData) !== ROLE_KIND.EDUCATOR) return;

    codeAttempted.current = true;
    assignEducatorCode();
  }, [userData, assignEducatorCode]);

  // Keep the public directory entry in step with the profile.
  //
  // The entry is written once, when the code is allocated. An educator who
  // adds a title afterwards — which is most of them, since `sirName` didn't
  // exist at signup until now — would otherwise keep greeting their class
  // under the old name, because the students read the directory, not the
  // educator's profile. It's a no-op when nothing has changed.
  useEffect(() => {
    const uid = getAuth().currentUser?.uid;
    if (!uid || !userData?.educatorCode) return;
    refreshDirectoryEntry(uid, userData);
  }, [userData]);
  const isTrialExpired =
    !accessLoading && !isPaid && userData?.trial?.status === "expired";

  const auth = getAuth();
  const db   = getFirestore();
  const navigate = useNavigate();

  // refs for coach marks
  const subjectRowRef     = useRef(null);
  const classroomBtnRef   = useRef(null);
  const avatarEditRef     = useRef(null);
  const educatorCodeRef   = useRef(null);
  const scienceBtnRef     = useRef(null); // 👈 Added this line

  // put this near other constants in DashboardEducator
// Turns a Firebase callable error into something an educator can act on.
// "Could not assign educator code: [object Object]" told nobody anything.
function describeCodeError(err) {
  const code = err?.code || "";

  if (code === "permission-denied") {
    return "Codes can't be issued yet — firestore.rules needs the counters/educatorCode update from the README deployed first.";
  }
  if (code === "unauthenticated") {
    return "Your sign-in expired. Sign out and back in, then try again.";
  }
  if (code === "unavailable" || code === "deadline-exceeded") {
    return "Couldn't reach the database. Check your connection and try again.";
  }

  return err?.message || "Couldn't assign an educator code. Please try again.";
}

const TUTORIAL_VIDEO_URL = "https://firebasestorage.googleapis.com/v0/b/sbleducationalportal.firebasestorage.app/o/videos%2Fsbltutorial.mp4?alt=media&token=36a16d7c-ef3a-4750-ae43-480fba8403d3"; // replace with your real URL

  // LIVE, not a one-shot read — same fix as Dashboard.js.
  //
  // The Stripe webhook promotes the role and writes the subscription a few
  // seconds after the redirect lands, so a single getDoc on mount showed the
  // pre-payment state indefinitely. It also read `auth.currentUser` before
  // Firebase had restored the session on a cold load, which is why a direct
  // visit sometimes rendered "No user data found."
  useEffect(() => {
    let unsubDoc = null;
    let expiryChecked = false;

    const unsubAuth = onAuthStateChanged(auth, (user) => {
      if (unsubDoc) {
        unsubDoc();
        unsubDoc = null;
      }

      if (!user) {
        setUserData(null);
        setLoading(false);
        return;
      }

      const userRef = doc(db, 'users', user.uid);

      unsubDoc = onSnapshot(
        userRef,
        (snap) => {
          if (!snap.exists()) {
            setUserData(null);
            setLoading(false);
            return;
          }

          const data = snap.data();
          setUserData(data);
          setLoading(false);

          // Once per mount: this writes back to the document being watched,
          // so running it on every snapshot would loop.
          if (!expiryChecked) {
            expiryChecked = true;
            checkAndExpireTrial(userRef, data).catch((err) =>
              console.warn('Trial expiry check failed:', err?.message || err)
            );
          }
        },
        (error) => {
          console.error('Error reading user data:', error.message);
          setLoading(false);
        }
      );
    });

    return () => {
      if (unsubDoc) unsubDoc();
      unsubAuth();
    };
  }, [auth, db]);


  function isTrialExpiredByDate(user) {
    const end = user?.trial?.endDate;
    if (!end) return false;
  
    const endDate = end?.toDate ? end.toDate() : new Date(end);
    return endDate.getTime() < Date.now();
  }
  
  // 🔥 Log page visit to Firestore when dashboard loads
useEffect(() => {
  logPageView("/educator/dashboard");
}, []);


  const dismissTips = () => setHideTips(true);

  // 👇 ADD THIS
  const showTips = () => setHideTips(false);


  if (loading) return <p>Loading...</p>;
  if (!userData) return <p>No user data found.</p>;

  const handleSubjectClick = (subject) => {
    // This used to be `userData.role.split("-")`, which only works once a
    // role has been granted. A free account's role is "unpaid", so that
    // produced /portal/unpaid/undefined/{subject}/educator and a dead page.
    //
    // Registration stores state and grade as their own fields, so prefer
    // those and fall back to parsing the role for accounts created before
    // the signup flow existed.
    const [roleState, roleGrade] = String(userData.role || "").split("-");
    const state = (userData.state || roleState || "").toLowerCase();
    const grade = userData.grade
      ? gradeToken(userData.grade)
      : roleGrade;

    if (!state || !grade) {
      console.warn("No state/grade on this account; sending to plan page.");
      navigate("/choose-plan");
      return;
    }

    navigate(`/portal/${state}/${grade}/${subject}/educator`);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error.message);
    }
  };

  const handleAvatarEdit = () => setShowAvatarPopup(true);
  const handleClosePopup = () => setShowAvatarPopup(false);

  // Educators build their avatar in the same avatar maker as students
  // (everything free for them); it saves through saveAvatarLook.

  
  // isUserBlocked() lived here and was never called from anywhere in this
  // file. Its logic now belongs to useAccessLevel, which reads the
  // subscription document, seat assignments, and the admin claim together.
  
  // Classroom pages are hardcoded per state and grade in App.js. Only these
  // exist — a state outside the list has no page to send anyone to.
  const CLASSROOM_STATES = ["va", "nc", "ca", "ga"];

  const handleClassroomNavigation = () => {
    // This used to be `userData.role.split("-")`, the same bug that broke the
    // subject buttons. A trial or free account's role is "unpaid", so the
    // split produced /educator/unpaid/unpaid-undefined-undefined-classroom —
    // a path no route matches, so the button appeared to do nothing.
    //
    // Registration stores state and grade as their own fields; the role is
    // only parsed as a fallback for accounts created before that.
    const [roleState, roleGrade] = String(userData.role || "").split("-");
    const state = (userData.state || roleState || "").toLowerCase();
    const grade = userData.grade ? gradeToken(userData.grade) : roleGrade;

    if (!state || !grade) {
      alert(
        "Your account doesn't have a state and grade set yet, so we can't open your classroom. Please contact support."
      );
      return;
    }

    // The CLASSROOM_STATES allowlist that used to sit here is gone. It
    // existed because classroom pages were hardcoded per state, so an
    // educator in any of the other ~45 states got an alert telling them the
    // feature wasn't built. EducatorClassroom reads state and grade off the
    // route, so there is nothing left to allowlist.

    // Was: /educator/${state}/${state}-${grade}-educator-classroom, which only
    // existed for the handful of states with a hardcoded page — every other
    // state hit the "not built yet" alert above. One route now, every state.
    navigate(`/educator/${state}/${grade}/classroom`);
  };

  // Labels and icons come from src/data/subjects.js so the dashboard and the
  // portal can never disagree about what a subject is called.
  const subjects = NAV_SUBJECTS;
  const colors = ["#007bff", "#28a745", "#dc3545", "#ffc107"];
  const backgroundImageUrl = userData?.backgroundImageUrl || "";

  return (
    <div
      style={{
        ...styles.dashboardContainer,
        ...(backgroundImageUrl
          ? {
              backgroundImage: `url(${backgroundImageUrl})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }
          : {}),
      }}
    >
      {isFree && <FreeTierBanner label="subjects" />}

      {isTrialExpired && (
        <div
          style={{
            background: "#fff3cd",
            border: "2px solid #ffcc00",
            padding: "12px 16px",
            marginBottom: 16,
            borderRadius: 8,
            fontWeight: 700,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>Your educator trial has ended.</span>
          <button
            onClick={() => navigate("/upgrade")}
            style={{
              background: "#0a6cff",
              color: "#fff",
              border: "none",
              padding: "6px 14px",
              borderRadius: 6,
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Upgrade Now
          </button>
        </div>
      )}
  
      {/* 🔽 everything else in your dashboard stays exactly the same below */}
  
      {/* Top card — same design as the student dashboard
          (components/dashboard/DashboardHero.js). */}
      <DashboardHero
        variant="educator"
        profile={userData}
        name={formalName(userData, "Teacher")}
        onEditAvatar={handleAvatarEdit}
        editRef={avatarEditRef}
        line={<EducatorHeroLine code={userData.educatorCode} />}
        chips={[
          <span ref={educatorCodeRef} key="code">
            {userData.educatorCode ? (
              <>
                🔑 Class code <code>{userData.educatorCode}</code>
                <CopyCode code={userData.educatorCode} />
              </>
            ) : codeState.status === 'working' ? (
              'Assigning your class code…'
            ) : (
              'Class code pending'
            )}
          </span>,
        ]}
        actions={
          <>
            {/* the teacher's own coins (daily visits) — for their own avatar */}
            <CoinCounter />
            <ShopButton educator />
            <BuyCoinsButton educator />
            <button type="button" className="sbl-hero-btn is-teal" onClick={handleClassroomNavigation} disabled={isTrialExpired}>
              🎁 Give coins
            </button>
            {userData.educatorCode && <LeaderboardButton code={userData.educatorCode} isEducator label="Class leaderboard" />}
          </>
        }
      >
        {!userData.educatorCode && codeState.status === 'error' && (
          <div style={styles.codeError}>
            <p style={{ margin: '0 0 8px' }}>{codeState.error}</p>
            <button type="button" onClick={retryEducatorCode} className="sbl-btn">
              Try again
            </button>
          </div>
        )}
      </DashboardHero>
      {/* reminder when the class coin bank runs low */}
      <LowClassBank />
      <EducatorDailyCoins />
      {/* admin promotions: coin discount / product code popups */}
      <EducatorPromos />

      {/* Choose a subject */}
      <section className="sbl-subjects">
        <header className="sbl-subjects-head">
          <p className="sbl-subjects-kicker">Teach</p>
          <h2 className="sbl-subjects-title">Choose a subject</h2>
          <p className="sbl-subjects-sub">Open a subject to teach with songs, videos and games — or open your Classroom to see each student's progress and give coins.</p>
        </header>

        <div className="sbl-subjects-row" ref={subjectRowRef}>
          {subjects.map((subject) => (
            <button
              key={subject.value}
              type="button"
              ref={subject.value === "science" ? scienceBtnRef : null}
              className={`sbl-tile ${subject.className}`}
              disabled={isTrialExpired}
              aria-disabled={isTrialExpired}
              title={isTrialExpired ? "Your trial has ended — upgrade to continue" : undefined}
              onClick={() => {
                if (isTrialExpired) {
                  navigate("/upgrade");
                  return;
                }
                handleSubjectClick(subject.value);
              }}
            >
              <span className="sbl-tile-icon" aria-hidden="true">
                {subject.icon}
              </span>
              <span className="sbl-tile-label">{subject.label}</span>
              {subject.hint && <span className="sbl-tile-hint">{subject.hint}</span>}
            </button>
          ))}

          {/* Classroom Button */}
          <button
            type="button"
            ref={classroomBtnRef}
            className="sbl-tile sbl-tile-classroom"
            disabled={isTrialExpired}
            aria-disabled={isTrialExpired}
            onClick={() => {
              if (isTrialExpired) {
                navigate("/upgrade");
                return;
              }
              handleClassroomNavigation();
            }}
          >
            <span className="sbl-tile-icon" aria-hidden="true">👩‍🏫</span>
            <span className="sbl-tile-label">Classroom</span>
            <span className="sbl-tile-hint">Students, progress & coins</span>
          </button>
        </div>
      </section>

      {/* Pay now instead of waiting for the trial to end. Gated on
          hasPaidSubscription, not isPaid — a trial counts as PAID for access,
          so isPaid would have hidden this from every trial user. */}
      {!hasPaidSubscription && (
        <>
          <SubscribeNow profile={userData} uid={getAuth().currentUser?.uid} />
          {/* The escape hatch for a payment the webhook never landed. */}
          <RefreshAccessButton />
        </>
      )}

      {/* Bottom, one centred column: tutorial & shop, then Logout + Account
          settings, then the "From the shop" banner underneath. */}
      <div className="sbl-dash-bottom">
        <div className="sbl-dash-bottom-row">
          <button type="button" className="sbl-btn" onClick={() => setShowTutorial(true)} aria-label="Open video tutorial">
            ▶️ Video Tutorial
          </button>
          <button type="button" className="sbl-btn sbl-btn-quiet" onClick={() => navigate('/products')}>
            🛍️ Visit the Shop
          </button>
          {userData?.roles?.includes('administrator') && (
            <button type="button" className="sbl-btn sbl-btn-quiet" onClick={() => navigate('/admin')}>
              Admin Dashboard
            </button>
          )}
        </div>
        <div className="sbl-dash-bottom-row">
          <button className="sbl-btn sbl-btn-danger sbl-btn-lg" onClick={handleLogout}>
            Logout
          </button>
          {/* Password, email and subscription (incl. cancelling) */}
          <button type="button" className="sbl-account-link" onClick={() => navigate("/account")}>
            ⚙️ Account settings
          </button>
        </div>
        <ProductSpotlight heading="From the shop" />
      </div>

      {showAvatarPopup && (
        <AvatarMaker isEducator onClose={handleClosePopup} />
      )}

      {/* Trial extension offer.
          Shown from 5 days out until the trial ends or the offer is taken.
          `trialState` already accounts for a prior extension, a recent
          dismissal, and whether Stripe has taken over the clock — see
          utils/trialOffer.js. The local `trialOfferDismissed` flag suppresses
          it for the rest of this page view; the server-side dismissal (below)
          suppresses it for 24h across sessions. */}
      <TrialOfferModal
        open={trialState.shouldOffer && !trialOfferDismissed}
        daysRemaining={trialState.daysRemaining}
        alreadyExtended={trialState.alreadyExtended}
        offerMonths={2}
        discountLabel="50% off your first year"
        onExtended={(result) => {
          setTrialOfferDismissed(true);
          // Reflect the new end date immediately rather than waiting for the
          // next profile read — the banner underneath this reads userData.
          if (result?.newEndDate) {
            setUserData((prev) => ({
              ...prev,
              trial: {
                ...(prev?.trial || {}),
                status: "active",
                endDate: new Date(result.newEndDate),
                extensions: [
                  ...((prev?.trial?.extensions) || []),
                  { months: result.months },
                ],
              },
            }));
          }
        }}
        onDismiss={async () => {
          setTrialOfferDismissed(true);
          try {
            const dismiss = httpsCallable(getFunctions(), "dismissTrialOffer");
            await dismiss({});
          } catch (err) {
            // A failed dismissal is cosmetic — worst case the offer returns
            // on the next page load. Not worth surfacing to the educator.
            console.warn("[TrialOffer] dismissal not saved:", err?.message);
          }
        }}
      />
{showTutorial && (
  <div style={styles.popupOverlay} role="dialog" aria-modal="true">
    <div style={styles.popupBox}>
      <button
        onClick={() => setShowTutorial(false)}
        style={styles.closeButton}
        aria-label="Close tutorial"
        title="Close"
      >
        ✖
      </button>
      <h2 style={{ marginTop: 0, color: "#111" }}>Quick Video Tutorial</h2>
      <video
        controls
        style={{ width: "100%", borderRadius: 8, outline: "none" }}
        src={TUTORIAL_VIDEO_URL}
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
      />
    </div>
  </div>
)}

      {/* Show Tips button (only when tips are hidden) */}
      {hideTips && (
        <button
          type="button"
          onClick={showTips}
          className="sbl-show-tips"
    style={overlayStyles.showButton}
        >
          Show Tips
        </button>
      )}

      {/* Footer */}
      <footer style={styles.footer}>
        <p style={{ margin: 0 }}>© Smart Board Learning™</p>
        <p style={{ margin: 0 }}>All rights reserved.</p>
      </footer>

      {/* Instruction overlays (do not change layout) */}
      {!hideTips && (
        <HintsOverlay
          canDismiss
          onDismiss={dismissTips}
          targets={{
            science: {
              ref: scienceBtnRef,
              placement: 'left',  // 👈 put bubble on the left side
              text: 'Choose Science to explore science topics.',
            },
            classroom: {
              ref: classroomBtnRef,
              placement: 'top',
              text:
                'Classroom shows progress for students who share your Educator Code.',
            },
            avatarEdit: {
              ref: avatarEditRef,
              placement: 'right',
              text:
                'Click “Edit” to add or change your avatar.',
            },
            educatorCode: {
              ref: educatorCodeRef,
              placement: 'bottom',
              text:
                'Your Educator Code is unique to your class. Students enter this on the student page to join your Classroom.',
            },
          }}
        />
      )}
    </div>
  );
};

/* -------- your existing styles unchanged (plus your styles object below) ---------- */
const styles = {
  dashboardContainer: {
    textAlign: 'center',
    padding: '20px',
    backgroundColor: '#e0f7ff',
    minHeight: '100vh',
  },
  logoContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  logo: { height: '80px' },
  centerContainer: {
    backgroundColor: '#fff',
    borderRadius: '10px',
    padding: '20px',
    margin: '20px auto',
    maxWidth: '600px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
  },
  avatarContainer: { position: 'relative', marginBottom: '20px' },
  avatar: {
    width: '100px', height: '100px',
    borderRadius: '50%', objectFit: 'cover',
  },
  avatarButton: {
    position: "absolute", bottom: "0", right: "0",
    width: "60px", height: "60px",
    backgroundColor: "#16a085", color: "#fff", fontSize: "18px",
    border: "2px solid #222", borderRadius: "50%", cursor: "pointer",
    display: "flex", justifyContent: "center", alignItems: "center",
    boxShadow: "0 4px 0 rgba(0, 0, 0, 0.3), 0 6px 8px rgba(0, 0, 0, 0.3)",
    transition: "transform 0.2s, box-shadow 0.2s",
  },
  subscriptionBadge: {
    backgroundColor: "#8e44ad", color: "#fff", fontWeight: "bold",
    fontSize: "16px", padding: "6px 12px", borderRadius: "6px",
    display: "inline-block", boxShadow: "0 4px 6px rgba(0,0,0,0.3)",
    border: "2px solid #222", margin: "10px 0",
  },
  welcomeText: { marginTop: '30px', fontSize: '16px' },
  subjectButtonContainer: {
    display: "flex", justifyContent: "center", flexWrap: "wrap",
    gap: "20px", marginTop: "70px",
  },
  subjectButton: {
    width: "175px", height: "175px",
    display: "flex", justifyContent: "center", alignItems: "center",
    cursor: "pointer", borderRadius: "5px", color: "#fff",
    fontSize: "25px", fontWeight: "bold", textAlign: "center",
    padding: "10px 20px", border: "2px solid #222",
    boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)",
    transition: "transform 0.2s, box-shadow 0.2s",
    backgroundColor: "#4a90e2",
  },
  buttonText: { color: "#fff" },
  scienceButton: { backgroundColor: "#4a90e2" },
  socialStudiesButton: { backgroundColor: "#4CAF50" },
  mathButton: { backgroundColor: "#FF6347" },
  otherButton: { backgroundColor: "#FFD700" },
  homeButton: { backgroundColor: "#A020F0" },
  classroomContainer: { marginTop: '30px', textAlign: 'center' },
  classroomButton: {
    backgroundColor: "#00CED1",
    border: "2px solid #005a5a",
    boxShadow: "0 6px 0 #005a5a, 0 8px 10px rgba(0,0,0,0.4)",
  },
  adminLinkContainer: { marginTop: '40px', textAlign: 'center' },
  adminButton: {
    backgroundColor: '#007bff', color: 'white', padding: '10px 20px',
    border: 'none', borderRadius: '5px', fontSize: '16px',
    cursor: 'pointer', transition: 'background-color 0.3s ease',
  },
  shopButton: {
    backgroundColor: '#7c3aed', color: 'white', padding: '10px 20px',
    border: 'none', borderRadius: '5px', fontSize: '16px',
    cursor: 'pointer', transition: 'background-color 0.3s ease',
  },
  codeError: {
    maxWidth: "420px",
    margin: "0 auto",
    padding: "12px 16px",
    borderRadius: "12px",
    background: "rgba(192, 57, 43, 0.08)",
    border: "1px solid rgba(192, 57, 43, 0.25)",
    color: "#8c2f22",
    fontSize: "14px",
    lineHeight: 1.5,
    textAlign: "center",
  },
  codeRetry: {
    border: 0,
    borderRadius: "999px",
    padding: "8px 18px",
    fontSize: "14px",
    fontWeight: 700,
    color: "#fff",
    cursor: "pointer",
    background: "linear-gradient(135deg, #e100ff, #7f00ff)",
  },
  logoutButton: {
    width: "200px", height: "75px", display: "flex",
    justifyContent: "center", alignItems: "center",
    padding: "10px 20px", fontSize: "22px", fontWeight: "bold",
    color: "#fff", backgroundColor: "#c0392b",
    border: "2px solid #222", borderRadius: "5px", cursor: "pointer",
    boxShadow: "0 6px 0 rgba(0, 0, 0, 0.4), 0 8px 10px rgba(0, 0, 0, 0.4)",
    transition: "transform 0.2s, box-shadow 0.2s",
    marginTop: "40px",
  },
  footer: {
    marginTop: "40px", fontSize: "16px", color: "#555",
    textAlign: "center", padding: "10px",
    backgroundColor: "#f9f9f9", borderTop: "1px solid #ddd",
  },
  tutorialButton: {
    backgroundColor: "#6c5ce7",          // distinct purple
    border: "2px solid #2d1edd",
    boxShadow: "0 6px 0 #2d1edd, 0 8px 10px rgba(0,0,0,0.4)",
  },

  // Modal styles (reuse if you already have from the other component)
  popupOverlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.55)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10001,
    padding: "20px",
  },
  popupBox: {
    // Without this a long lyric sheet pushes the box off-screen. Content
    // scrolls inside the popup now; the popup itself never exceeds the
    // viewport.
    maxHeight: "90vh",
    overflowY: "auto",
    backgroundColor: "#fff",
    width: "min(960px, 95vw)",
    borderRadius: "10px",
    padding: "18px 18px 24px",
    position: "relative",
    boxShadow: "0 16px 40px rgba(0,0,0,.35)",
  },
  closeButton: {
    position: "absolute",
    top: 10,
    right: 10,
    backgroundColor: "#ff4d4d",
    color: "#fff",
    border: "none",
    borderRadius: "50%",
    width: 34,
    height: 34,
    fontSize: 18,
    cursor: "pointer",
    lineHeight: "34px",
    textAlign: "center",
  },
  
};

export default DashboardEducator;
